"use client";

import * as React from "react";
import Link from "next/link";

import { cn } from "@/lib/utils";

/**
 * Canonical title + subtitle treatment for an entity list-item, card,
 * or section header. Used anywhere a "name" needs to read as a title —
 * JobEntity title, CompanyEntity name, ContactEntity fullName, section
 * headers ("Contacts at X"), etc.
 *
 * One primitive -> one visual treatment -> consistent hierarchy.
 */

type Size = "sm" | "md" | "lg";

type PolymorphicAs =
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "span"
  | "div"
  | "p";

export interface EntityTitleProps {
  size?: Size;
  as?: PolymorphicAs;
  href?: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  truncate?: boolean;
  lineClamp?: 1 | 2 | 3;
  suffix?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

/**
 * Firm but organic title sizes. We bypass the semantic tokens here so the
 * render doesn't depend on Tailwind v4 utility resolution at build time.
 *
 *   sm -> 15px / 700   (row titles)
 *   md -> 17px / 700   (list-item default)
 *   lg -> 20px / 800   (section / page sub-headers)
 */
const SIZE_CLASSES: Record<Size, string> = {
  sm: "text-[15px] font-bold leading-snug tracking-tight",
  md: "text-[17px] font-bold leading-snug tracking-tight",
  lg: "text-xl font-extrabold leading-tight tracking-tight",
};

const CLAMP_CLASSES: Record<NonNullable<EntityTitleProps["lineClamp"]>, string> =
  {
    1: "line-clamp-1",
    2: "line-clamp-2",
    3: "line-clamp-3",
  };

export function EntityTitle({
  size = "md",
  as = "h3",
  href,
  onClick,
  truncate = false,
  lineClamp,
  suffix,
  children,
  className,
}: EntityTitleProps) {
  const Element = as as React.ElementType;
  const interactive = Boolean(href || onClick);

  const classes = cn(
    SIZE_CLASSES[size],
    "text-foreground break-words min-w-0",
    lineClamp ? CLAMP_CLASSES[lineClamp] : null,
    truncate && !lineClamp ? "truncate" : null,
    interactive &&
      "cursor-pointer transition-colors hover:text-[var(--burgundy-core)]",
    className,
  );

  const label = (
    <span
      className={cn(
        lineClamp ? "block" : null,
        truncate ? "block truncate" : null,
      )}
    >
      {children}
    </span>
  );

  const inner = (
    <>
      {label}
      {suffix ? (
        <span className="ml-2 inline-flex shrink-0 items-center align-middle">
          {suffix}
        </span>
      ) : null}
    </>
  );

  if (href) {
    return (
      <Element className={classes} onClick={onClick}>
        <Link href={href} className="no-underline hover:no-underline">
          {inner}
        </Link>
      </Element>
    );
  }

  if (onClick) {
    return (
      <Element
        className={classes}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick(e as unknown as React.MouseEvent<HTMLElement>);
          }
        }}
      >
        {inner}
      </Element>
    );
  }

  return <Element className={classes}>{inner}</Element>;
}

/* ------------------------------ EntitySubtitle ------------------------ */

export interface EntitySubtitleProps {
  /**
   * foreground - same color as body (default)
   * muted      - dimmer, for secondary metadata
   * primary    - burgundy, for emphasized section-like labels ("Why Contact")
   */
  tone?: "foreground" | "muted" | "primary";
  as?: PolymorphicAs;
  href?: string;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  truncate?: boolean;
  lineClamp?: 1 | 2 | 3;
  children: React.ReactNode;
  className?: string;
}

/**
 * Secondary title — same size as body (13px) but weight 600 so it reads
 * as emphasized metadata. Use for the company under a job title, the
 * "title, company" line under a contact name, etc.
 */
export function EntitySubtitle({
  tone = "foreground",
  as = "p",
  href,
  onClick,
  truncate = false,
  lineClamp,
  children,
  className,
}: EntitySubtitleProps) {
  const Element = as as React.ElementType;
  const interactive = Boolean(href || onClick);

  const toneClass =
    tone === "muted"
      ? "text-muted-foreground"
      : tone === "primary"
        ? "text-primary"
        : "text-foreground";

  const classes = cn(
    "text-[13px] font-semibold leading-snug",
    toneClass,
    lineClamp ? CLAMP_CLASSES[lineClamp] : null,
    truncate && !lineClamp ? "truncate" : null,
    interactive &&
      "cursor-pointer transition-colors hover:text-[var(--burgundy-core)]",
    className,
  );

  const content =
    lineClamp || truncate ? <span className="block">{children}</span> : children;

  if (href) {
    return (
      <Element className={classes} onClick={onClick}>
        <Link href={href} className="no-underline hover:no-underline">
          {content}
        </Link>
      </Element>
    );
  }

  if (onClick) {
    return (
      <Element className={classes} onClick={onClick}>
        {content}
      </Element>
    );
  }

  return <Element className={classes}>{content}</Element>;
}
